import { getUserInformation } from "@/api/signin";
import type { UserInformation } from "@/api/signin";
import { ENV } from "@/axios/constants";
import { axiosInstance } from "@/axios/interceptors";
import { parseRequestError } from "@/axios/utils.ts";
import { ZodSentryReporter } from "@/lib/zod-sentry-reporter";
import { z } from "zod";

export type RegisterUserPayload = {
	email: string;
	password: string;
	phone_global_country_id: number;
	phone_number: number;
	email_verification_callback: string;
	promo_code?: string;
	business_name?: string;
};

export const BASE_PAYLOAD = {
	send_2fa: false,
};

export const BASE_USER_ID = {
	user_id: ENV.VITE_USER_ID,
};

export type AuthTokenResponse = {
	access_token: string;
	expires_in: number;
};

export type RegisterUserResponse = {
	email: string;
	phone_global_country_id: number;
	global_country_id: number;
	phone: string;
	id: number;
	auth_token: AuthTokenResponse;
};

export type UpdateUserResponse = {
	email: string;
	phone_global_country_id: number;
	global_country_id: number;
	phone: string;
	id: number;
	address?: string;
	apt_number?: string;
	city?: string;
	zipcode?: string;
	first_name?: string;
	last_name?: string;
	date_of_birth?: string;
};

export const registerUser = async (
	payload: RegisterUserPayload,
): Promise<RegisterUserResponse> => {
	const { business_name, ...rest } = payload;
	const response = await axiosInstance.post("/guests/kashu", {
		...rest,
		...BASE_USER_ID,
		send_2fa: false,
		...(business_name ? { business_name: business_name.trim() } : {}),
	});
	return response.data;
};

const ValidateSignUpAccessCodeSchema = z.object({
	valid: z.boolean(),
});

export type ValidateSignUpAccessCodePayload = {
	accessCode: string;
};

export type ValidateSignUpAccessCodeResponse = z.infer<
	typeof ValidateSignUpAccessCodeSchema
>;

export const validateSignUpAccessCode = async ({
	accessCode,
}: ValidateSignUpAccessCodePayload) => {
	const response = await axiosInstance.post(
		"/guests/kashu/validate-sign-up-code",
		{ promo_code: accessCode },
	);
	return ZodSentryReporter.parse(ValidateSignUpAccessCodeSchema, response);
};

export type SendPhoneVerificationCodePayload = {
	phone_number: string;
	global_country_id: number;
};

export const sendPhoneVerificationCode = async (
	payload: SendPhoneVerificationCodePayload,
	locationId: number,
): Promise<UpdateUserResponse> => {
	const response = await axiosInstance.post(
		`/locations/${locationId}/mobile/send-2fa`,
		{
			subscribe: false,
			type: "2fa",
			...payload,
		},
	);
	return response.data;
};

export type PhoneVerificationCodeResponse = {
	guest_id: number;
	registration_completed: boolean;
};

export type VerifyPhoneVerificationCodePayload = {
	phone_number: number;
	verification_code: number;
	phone_global_country_id: number;
	global_country_id: number;
};

const VerifyPhoneVerificationSchema = z.object({
	guest_id: z.number(),
});

export type VerifyPhone = z.infer<typeof VerifyPhoneVerificationSchema>;

export type AlternativeAuthParams = {
	guestId: number;
};

export const enableFaceId = async ({
	guestId,
}: AlternativeAuthParams): Promise<UpdateUserResponse> => {
	const response = await axiosInstance.put(`/guests/${guestId}`, {
		face_id: true,
		...BASE_USER_ID,
	});

	return response.data;
};

export const createPinCode = async (
	{ guestId }: AlternativeAuthParams,
	pinCode: string,
): Promise<UpdateUserResponse> => {
	const response = await axiosInstance.put(`/guests/${guestId}`, {
		pin: pinCode,
		...BASE_USER_ID,
	});

	return response.data;
};

export type UpdateGuestPhonePayload = {
	phone: string;
	phone_global_country_id: number;
	global_country_id: number;
};

export const updateGuestPhone = async (
	guestId: number,
	payload: UpdateGuestPhonePayload,
): Promise<UserInformation> => {
	await axiosInstance.put(`/guests/${guestId}`, {
		...payload,
		...BASE_USER_ID,
	});
	return getUserInformation();
};

export type UpdateGuestEmailPayload = {
	email: string;
};

export const updateGuestEmail = async (
	guestId: number,
	payload: UpdateGuestEmailPayload,
): Promise<UserInformation> => {
	await axiosInstance.put(`/guests/${guestId}`, {
		...payload,
		...BASE_USER_ID,
	});
	return getUserInformation();
};

export type UpdateGuestBusinessNamePayload = {
	business_name: string;
};

export const updateGuestBusinessName = async (
	guestId: number,
	payload: UpdateGuestBusinessNamePayload,
) => {
	await axiosInstance.put(`/guests/${guestId}`, {
		...payload,
		...BASE_USER_ID,
	});
};

export type UpdateGuestDetailResponse = {
	id: number;
};

export type UpdateGuestPersonalDetailPayload = {
	first_name: string;
	last_name: string;
	date_of_birth: string;
	ssn: string;
};

export type UpdateGuestHomeAddressPayload = {
	address: string;
	apt_number: string;
	city: string;
	global_state_id: number;
	zipcode: string;
};

/**
 * @deprecated Personal info is now display-only and cannot be updated via this endpoint.
 * Use UpdateGuestPersonalDetailPayload or UpdateGuestHomeAddressPayload for separate updates instead.
 */
export type UpdateGuestPersonalInfoSettingPayload = {
	first_name: string;
	last_name: string;
	date_of_birth: string;
	email: string;
	phone: string;
	global_country_id: number;
	address: string;
	apt_number: string;
	city: string;
	global_state_id: number;
	zipcode: string;
	phone_global_country_id: number;
};

export type ChangePinPayload = {
	current_pin: string;
	new_pin: string;
};

export type ChangePasswordPayload = {
	current_password: string;
	new_password: string;
};

export const GuestDetailsSchema = z.object({
	id: z.number(),
	email: z.string().email(),
	phone_number: z.string(),
	first_name: z.string(),
	last_name: z.string(),
	date_of_birth: z.string(), // Consider using z.string().datetime() or custom date validation
	address: z.string(),
	apt_number: z.string(),
	city: z.string(),
	zipcode: z.string(),
	business_name: z.string().nullable(),
	global_state_id: z.number(),
	state: z
		.object({
			id: z.number(),
			name: z.string(),
			code: z.string(),
			global_country_id: z.number().nullable(),
		})
		.nullable(),
	global_country_id: z.number(),
	location_registration: z.null().or(z.unknown()), // Adjust based on expected type when not null
});

type AcceptTerms = {
	accepted_terms_and_policies: boolean;
};

export type GuestDetails = z.infer<typeof GuestDetailsSchema>;

export type GuestDetailsPayload =
	| UpdateGuestPersonalDetailPayload
	| UpdateGuestHomeAddressPayload
	| ChangePasswordPayload
	| ChangePinPayload
	| AcceptTerms;

export const updateGuestDetails = async (
	guestId: number,
	payload: GuestDetailsPayload,
) => {
	const response = await axiosInstance.put(`/guests/${guestId}`, {
		...payload,
	});
	return ZodSentryReporter.parse(GuestDetailsSchema, response);
};

export type TwoFactorAuthPayload = {
	email: string;
	verification_code: number;
	type: "2fa" | "phone";
	phone_number: string;
	global_country_id: number;
	phone_global_country_id: number;
};

export const validateTwoFactorAuthenticationCode = async (
	payload: TwoFactorAuthPayload,
	locationId: number,
) => {
	const response = await axiosInstance.post(
		`/locations/${locationId}/mobile/guest-registration-validation`,
		{ ...payload },
	);
	return ZodSentryReporter.parse(VerifyPhoneVerificationSchema, response);
};

export type SendEmailVerificationCodePayload = {
	email: string;
	phone_number: string;
	global_country_id: number;
};

export const sendEmailVerificationCode = async (
	payload: SendEmailVerificationCodePayload,
	locationId: number,
) => {
	console.log(payload);
	const response = await axiosInstance.post(
		`/locations/${locationId}/mobile/send-2fa-email`,
		{
			...payload,
			subscribe: false,
		},
	);
	return response.data;
};

const FormOptionsResponseSchema = z.object({
	goal_options: z.array(
		z.object({
			id: z.number(),
			code: z.string(),
			name: z.string(),
		}),
	),
});

export type FormOptionsResponse = z.infer<typeof FormOptionsResponseSchema>;
export type GuestGoalOptions = FormOptionsResponse["goal_options"];
export type GuestGoalOption = GuestGoalOptions[number];

export const getRegistrationFormOptions = async () => {
	try {
		const response = await axiosInstance.get(
			"/kashu_registrations/form_options",
		);
		return ZodSentryReporter.parse(FormOptionsResponseSchema, response);
	} catch (error) {
		throw parseRequestError(error);
	}
};

export type UpdateGuestFormRequest = {
	goal_option_id: number;
};

export const updateGuestRegistrationForm = async (
	request: UpdateGuestFormRequest,
	locationId: number,
) => {
	try {
		await axiosInstance.put(
			`/locations/${locationId}/guest_registration_from`,
			request,
		);
		return;
	} catch (error) {
		throw parseRequestError(error);
	}
};
